import { batch, createEffect, on, onCleanup, onMount } from "solid-js";

import { type Client, File } from "stoat.js";

import { asleep } from "@revolt/ui/components/utils/sleep";

/** How often who is online is asked again while the member list is on screen */
export const PRESENCE_SYNC_MS = 15_000;

type OnlineList = {
  users: {
    _id: string;
    online?: boolean;
    status?: unknown;
    avatar?: Exclude<ConstructorParameters<typeof File>[1], File>;
  }[];
};

/**
 * Konkord: who is online according to the server, over what this app believes.
 * Presence only arrives as single events: one lost, or two crossing (a browser
 * tab reloading sends "online" and then the old tab's "offline"), and someone
 * stayed offline for us until the app was reopened.
 */
export async function syncOnline(client: Client, serverId: string) {
  const { users } = (await client.api.get(
    `/servers/${serverId as ""}/members`,
    {
      exclude_offline: true,
    },
  )) as OnlineList;
  const online = new Map(users.map((user) => [user._id, user]));

  const ids = new Set(online.keys());
  for (const member of client.serverMembers.values())
    if (member.id.server === serverId) ids.add(member.id.user);

  batch(() => {
    for (const id of ids) {
      const user = client.users.get(id);
      if (!user || id === client.user?.id) continue;
      const now = online.get(id);
      if (user.online !== !!now)
        client.users.updateUnderlyingObject(id, "online", !!now);
      if (
        now?.status &&
        JSON.stringify(user.status) !== JSON.stringify(now.status)
      )
        client.users.updateUnderlyingObject(id, "status", now.status as never);
      // Konkord: Stoat only tells about a new photo to friends and the like;
      // someone else's stayed as it was until the app was reopened
      if (now && (now.avatar?._id ?? undefined) !== user.avatar?.id)
        client.users.updateUnderlyingObject(
          id,
          "avatar",
          now.avatar && (new File(client, now.avatar) as never),
        );
    }
  });
}

/**
 * Konkord: keeps who is online right while a server's member list is on screen.
 * Stoat only tells us by itself about friends and people we talk to; everyone
 * else in a group has to be asked. Nobody is asked while nobody is looking: with
 * the app hidden or untouched for a few minutes (`asleep`) it stops, and asks
 * once when someone comes back.
 */
export function createPresenceSync(
  client: () => Client,
  serverId: () => string | undefined,
) {
  const sync = () => {
    const id = serverId();
    if (id && !asleep()) void syncOnline(client(), id).catch(() => {});
  };
  onMount(() => {
    sync();
    const timer = setInterval(sync, PRESENCE_SYNC_MS);
    onCleanup(() => clearInterval(timer));
  });
  createEffect(
    on(
      asleep,
      (resting, wasResting) => {
        if (wasResting && !resting) sync();
      },
      { defer: true },
    ),
  );
}
